import { Pencil, Plus, Trash2 } from "lucide-mui";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Switch from "@mui/material/Switch";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import toast from "react-hot-toast";
import { api, type AgentSkill, type AgentSkillCreate } from "../../../api/client.ts";
import { keys } from "../../../api/queryKeys.ts";
import { useTenant } from "../../../contexts/TenantContext.tsx";
import IdentityCell from "../../../components/IdentityCell.tsx";
import RowActions from "../../../components/RowActions.tsx";
import ScopeChip from "../../../components/ScopeChip.tsx";
import SkillEditDialog, { type SkillFormValues } from "../../../components/SkillEditDialog.tsx";
import EmptyTableRow from "../../../components/table/EmptyTableRow.tsx";
import ScrollTable from "../../../components/table/ScrollTable.tsx";
import TableCard from "../../../components/table/TableCard.tsx";
import PageHeader from "../../../components/PageHeader.tsx";

function SkillsPage() {
  const queryClient = useQueryClient();
  const { selectedTenantId, tenants } = useTenant();
  const [editing, setEditing] = useState<AgentSkill | null>(null);
  const [creating, setCreating] = useState(false);

  const tenantNameById = new Map(tenants.map((t) => [t.id, t.name]));

  const queryKey = keys.agentSkill.list(selectedTenantId, true);
  const { data: skills, isLoading } = useQuery({
    queryKey,
    queryFn: () =>
      api.getAgentSkills({
        tenant_id: selectedTenantId ?? undefined,
        include_global: true,
      }),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: keys.agentSkill.all });

  const createMutation = useMutation({
    mutationFn: (body: AgentSkillCreate) => api.createAgentSkill(body),
    onSuccess: () => {
      invalidate();
      toast.success("Skill created");
      setCreating(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, values }: { id: string; values: Partial<SkillFormValues> }) =>
      api.updateAgentSkill(id, values),
    onSuccess: () => {
      invalidate();
      toast.success("Skill saved");
      setEditing(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteAgentSkill(id),
    onSuccess: () => {
      invalidate();
      toast.success("Skill deleted");
    },
  });

  const handleSubmit = (values: SkillFormValues) => {
    if (editing) {
      const { tenant_id: _tenantId, ...patch } = values;
      updateMutation.mutate({ id: editing.id, values: patch });
    } else {
      createMutation.mutate(values);
    }
  };

  const handleToggleEnabled = (skill: AgentSkill, next: boolean) => {
    updateMutation.mutate({ id: skill.id, values: { enabled: next } });
  };

  return (
    <>
      <PageHeader
        title="Agent skills"
        description={
          <>
            Markdown skills served to MCP clients as prompts and injected into the autonomous
            prospecting agent. The "when to use" hint is preloaded; the body is fetched on demand.
          </>
        }
        actions={
          <Button variant="contained" startIcon={<Plus />} onClick={() => setCreating(true)}>
            New skill
          </Button>
        }
      />

      <TableCard loading={isLoading}>
        <ScrollTable>
          <TableHead>
            <TableRow>
              <TableCell>Skill</TableCell>
              <TableCell>Scope</TableCell>
              <TableCell>When to use</TableCell>
              <TableCell sx={{ width: 80 }}>Enabled</TableCell>
              <TableCell padding="checkbox" />
            </TableRow>
          </TableHead>
          <TableBody>
            {skills?.length === 0 && (
              <EmptyTableRow colSpan={5}>
                No skills yet. Create one to teach the agent a procedure or domain rule.
              </EmptyTableRow>
            )}
            {(skills ?? []).map((skill) => (
              <TableRow
                key={skill.id}
                hover
                sx={{ cursor: "pointer" }}
                onClick={() => setEditing(skill)}
              >
                <TableCell sx={{ maxWidth: 360 }}>
                  <IdentityCell
                    name={skill.title}
                    caption={<Box component="span" sx={{ fontFamily: "monospace" }}>{skill.slug}</Box>}
                  />
                </TableCell>
                <TableCell>
                  <ScopeChip
                    tenantId={skill.tenant_id}
                    tenantName={skill.tenant_id ? tenantNameById.get(skill.tenant_id) : undefined}
                  />
                </TableCell>
                <TableCell>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {skill.description}
                  </Typography>
                </TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <Switch
                    size="small"
                    checked={skill.enabled}
                    onChange={(e) => handleToggleEnabled(skill, e.target.checked)}
                  />
                </TableCell>
                <TableCell padding="checkbox" align="right">
                  <RowActions
                    label={skill.title}
                    actions={[
                      { label: "Edit", icon: <Pencil fontSize="small" />, onClick: () => setEditing(skill) },
                      {
                        label: "Delete",
                        icon: <Trash2 fontSize="small" />,
                        destructive: true,
                        onClick: () => deleteMutation.mutateAsync(skill.id),
                        confirm: {
                          title: `Delete skill: ${skill.title}`,
                          body: (
                            <>
                              <code>{skill.slug}</code> is removed for good; this cannot be undone.
                            </>
                          ),
                        },
                      },
                    ]}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </ScrollTable>
      </TableCard>

      {(creating || editing) && (
        <SkillEditDialog
          initial={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSubmit={handleSubmit}
          saving={createMutation.isPending || updateMutation.isPending}
        />
      )}

    </>
  );
}

export default SkillsPage;
